/**
 * Handle error: extracts error messages and scrolls to the relevant element.
 * @param {string|Element} selector - Selector or element to scroll to
 * @param {string|Element} closestSelector - Selector or element for closest scroll context
 * @param {object} errors - Error object to extract messages from
 * @returns {string[]} Array of error messages
 */
export function handleError(selector, closestSelector = null, errors, isScrollTo = true) {
  const element = (typeof selector === 'object') ? selector : selector ? document.querySelector(selector) : null
  const returnErrors = []

  const extractErrors = (errorObj) => {
    if (typeof errorObj === 'object' && errorObj !== null) {
      for (const key in errorObj) {
        extractErrors(errorObj[key])
      }
    } else if (errorObj) {
      returnErrors.push(errorObj)
    }
  }

  extractErrors(errors)

  const closestElement = (typeof closestSelector === 'object') 
    ? closestSelector 
    : closestSelector 
      ? document.querySelector(closestSelector) 
      : element

  const selectorTo = closestElement || 'body'
  
  if (typeof scrollTo === 'function' && isScrollTo) {
    scrollTo('', selectorTo, -200, element)
  }

  return returnErrors?.length ? returnErrors : false
}

/**
* Start Validate Form
*/
export function startValidateForm (selector, stopValidate = false) { 
  const element = (typeof selector === 'object') ? selector : selector ? document.querySelector(selector) : ''
  if (!stopValidate) {
    element.querySelectorAll('.js-startValidateForm').forEach((item) => {
      item.click()
    })
  }

  return element
}
  
/**
* Validate Form
*/
export function validateForm (selector, closestSelector = '', valid = true) {
  const element = startValidateForm(selector)

  return new Promise((resolve, reject) => {
    if (valid) {
      resolve(true)
    } else {
      setTimeout(() => {
        const selectorNew = element.querySelectorAll('.invalid')[0]
        const closestElement = (typeof closestSelector === 'object') ? closestSelector : closestSelector ? document.querySelector(closestSelector) : selectorNew

        let selectorTo = 'body'
        if (closestElement) {
          selectorTo = closestElement
        }

        scrollTo('', selectorTo, -110, selectorTo)

        reject(false)
      }, 100)
    }
  })
}

/**
* Validate Corporate Email
*/
export function validateCorporateEmail (email) {
  const regularEmailsDomains = [
    '126.com', '163.com', '21cn.com', 'aim.com', 'alice.it', 'aliceadsl.fr', 'aliyun.com', 'aol.com', 'aol.it', 'arcor.de',
    'arnet.com.ar', 'att.net', 'bellsouth.net', 'bigpond.com', 'bigpond.net.au', 'bluewin.ch', 'blueyonder.co.uk', 'bol.com.br', 'bt.com', 'btinternet.com',
    'centurytel.net', 'charter.net', 'chello.nl', 'club-internet.fr', 'comcast.net', 'cox.com', 'cox.net', 'cox.net', 'daum.net', 'earthlink.net',
    'email.com', 'email.it', 'email.tst', 'facebook.com', 'fastmail.fm', 'fibertel.com.ar', 'foxmail.com', 'free.fr', 'freenet.de', 'freeserve.co.uk',
    'frontiernet.net', 'games.com', 'globo.com', 'globomail.com', 'gmail.co', 'gmail.com', 'gmail.com', 'gmx.com', 'gmx.de', 'gmx.fr',
    'gmx.net', 'google.com', 'googlemail.com', 'hanmail.net', 'hetnet.nl', 'home.nl', 'hotmail.be', 'hotmail.co.uk', 'hotmail.com', 'hotmail.com.ar',
    'hotmail.com.br', 'hotmail.com.mx', 'hotmail.de', 'hotmail.es', 'hotmail.fr', 'hotmail.it', 'hush.com', 'hushmail.com', 'icloud.com', 'ig.com.br',
    'iname.com', 'inbox.com', 'itelefonica.com.br', 'juno.com', 'keemail.me', 'laposte.net', 'lavabit.com', 'libero.it', 'list.ru', 'live.be',
    'live.ca', 'live.co.uk', 'live.com', 'live.com.ar', 'live.com.au', 'live.com.mx', 'live.de', 'live.fr', 'live.it', 'live.nl',
    'love.com', 'mac.com', 'mac.com', 'mail.com', 'mail.ru', 'mailbox.com', 'mailbox.org', 'me.com', 'msn.com', 'nate.com',
    'naver.com', 'netzero.net', 'neuf.fr', 'ntlworld.com', 'o2.co.uk', 'oi.com.br', 'online.de', 'optonline.net', 'optusnet.com.au', 'orange.fr',
    'orange.net', 'outlook.com', 'outlook.com.br', 'planet.nl', 'pobox.com', 'poste.it', 'prodigy.net.mx', 'protonmail.ch', 'protonmail.com', 'qq.com',
    'r7.com', 'rambler.ru', 'rediffmail.com', 'rocketmail.com', 'safe-mail.net', 'sbcglobal.net', 'sfr.fr', 'shaw.ca', 'sina.cn', 'sina.com',
    'sky.com', 'skynet.be', 'speedy.com.ar', 'sympatico.ca', 't-online.de', 'talktalk.co.uk', 'telenet.be', 'teletu.it', 'telus.net', 'terra.com.br',
    'tin.it', 'tiscali.co.uk', 'tiscali.it', 'tuta.io', 'tutamail.com', 'tutanota.com', 'tutanota.de', 'tvcablenet.be', 'uol.com.br', 'verizon.net',
    'virgilio.it', 'virgin.net', 'virginmedia.com', 'voila.fr', 'voo.be', 'wanadoo.co.uk', 'wanadoo.fr', 'web.de', 'windstream.net', 'wow.com',
    'ya.ru', 'yahoo.ca', 'yahoo.co.id', 'yahoo.co.in', 'yahoo.co.jp', 'yahoo.co.kr', 'yahoo.co.uk', 'yahoo.com', 'yahoo.com.ar', 'yahoo.com.au',
    'yahoo.com.br', 'yahoo.com.mx', 'yahoo.com.ph', 'yahoo.com.sg', 'yahoo.de', 'yahoo.fr', 'yahoo.it', 'yandex.com', 'yandex.ru', 'yeah.net',
    'ygm.com', 'ymail.com', 'zipmail.com.br', 'zoho.com', 'zonnet.nl'
  ]

  if (!email || typeof email !== 'string') return false

  const domain = email.split('@')[1]?.toLowerCase()

  if (!domain) return false

  return !regularEmailsDomains.includes(domain)
}
